import { useState, useEffect, useCallback, useMemo } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import dashboardImg from "../img/dashboard-doc.png";
import CountUp from "react-countup";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import CaseListModal from "../components/Dashboard/CaseListModal";
import TravelStatusModal from "../components/Common/TravelStatusModal";
import DashboardAnalytics from "../components/Dashboard/DashboardAnalytics";
import ActionRequiredSection, {
  mergeActionRequiredItems,
} from "../components/Dashboard/ActionRequiredSection";
import ReferralDashboard from "../components/Dashboard/ReferralDashboard";
import {
  activityIcon,
  addPatientNotes,
  agingToPriority,
  changePatientStatus,
  fetchCaseList,
  fetchDailySummary,
  fetchDashboard,
  formatWaitingDuration,
  getWaitingHours,
  navigateToRecord,
  PRIORITY_ENDPOINTS,
  severityToDot,
  TODAY_WORK_ENDPOINTS,
  updatePatientAgingStage,
} from "../services/dashboardApi";
import {
  normalizeAgingStage,
  normalizePatientStatus,
  getPatientStatusLabel,
  getPatientStatusBadgeKey,
  isTravelStatus,
  PATIENT_AGING_STAGE_OPTIONS,
  PATIENT_STATUS_OPTIONS,
} from "../utils/patientStatus";
import {
  fetchMyCrmDashboardPermissions,
  extractPermissionsList,
  isCrmDashboardSectionAllowed,
  isCrmDashboardAnySectionAllowed,
  isCrmDashboardItemAllowed,
} from "../services/dashboardPermissionsApi";

const formatRecentActivityTime = (item = {}) => {
  if (item?.time) return item.time;
  if (!item?.performedAt) return "";
  const date = new Date(item.performedAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const priorityBadgeLabel = {
  critical: "Critical",
  high: "High Priority",
  warning: "Warning",
  normal: "Normal",
};

const getPatientPhone = (row = {}) => {
  const raw = row?.raw || row || {};
  const code = String(
    row?.phoneCode ||
    row?.dial_code ||
    row?.dialCode ||
    row?.countryCode ||
    row?.country_code ||
    row?.phone_code ||
    raw?.phoneCode ||
    raw?.dial_code ||
    raw?.dialCode ||
    raw?.countryCode ||
    raw?.country_code ||
    raw?.phone_code ||
    raw?.patient?.phoneCode ||
    raw?.patient?.dial_code ||
    raw?.patient?.dialCode ||
    raw?.patient?.countryCode ||
    raw?.patient?.country_code ||
    raw?.patient?.phone_code ||
    "",
  ).trim();

  const phone = String(
    row?.emergency_contact_no ||
    row?.emergency_contact ||
    row?.patient_emergency_contact_no ||
    row?.phone ||
    row?.phoneNumber ||
    row?.phone_number ||
    row?.mobile ||
    row?.mobileNumber ||
    row?.mobile_number ||
    row?.contact ||
    row?.contact_no ||
    row?.patient_phone ||
    row?.patientPhone ||
    row?.patientMobile ||
    raw?.emergency_contact_no ||
    raw?.emergency_contact ||
    raw?.patient_emergency_contact_no ||
    raw?.phone ||
    raw?.phoneNumber ||
    raw?.phone_number ||
    raw?.mobile ||
    raw?.mobileNumber ||
    raw?.mobile_number ||
    raw?.contact ||
    raw?.contact_no ||
    raw?.patient_phone ||
    raw?.patientPhone ||
    raw?.patientMobile ||
    raw?.patient?.emergency_contact_no ||
    raw?.patient?.emergency_contact ||
    raw?.patient?.patient_emergency_contact_no ||
    raw?.patient?.mobile ||
    raw?.patient?.phone ||
    raw?.patient?.phoneNumber ||
    "",
  ).trim();

  if (code && phone) {
    if (phone.startsWith(code)) {
      return phone;
    }
    return `${code} ${phone}`;
  }
  return phone || code || "—";
};

const normalizeAgingRow = (row) => {
  const waitingHours = getWaitingHours(row);
  const waitingLabel = formatWaitingDuration(
    row.waitingLabel || waitingHours,
  );
  const priority = agingToPriority(row);
  const phone = getPatientPhone(row);
  return {
    id: row.patientId || row.enquiryId || row.patientName,
    patientId: row.patientId,
    enquiryId: row.enquiryId,
    name: row.patientName || row.patient_name || "—",
    phone,
    status: row.p_status || row.status || "—",
    stage: row.stageLabel || row.waitingLabel || "—",
    waitingLabel,
    waitingHours,
    priority,
    priorityLabel: row.priority || priorityBadgeLabel[priority] || "—",
    recordType: row.patientId ? "patient" : row.enquiryId ? "enquiry" : null,
    raw: row,
  };
};

const escapeCsvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const escapeHtmlCell = (value) => {
  const text = value == null ? "" : String(value);
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const downloadAgingExcel = (rows = []) => {
  const headers = [
    "Sr.No.",
    "Patient Name",
    "Patient ID",
    "Enquiry ID",
    "Phone Number",
    "Status",
    "Current Stage",
    "Waiting Time",
    "Priority",
    "Notes Count",
  ];
  const headerRow = headers
    .map(
      (header) =>
        `<th style="background-color:#0ba6df;color:#ffffff;font-weight:bold;padding:8px;border:1px solid #ddd;">${escapeHtmlCell(
          header,
        )}</th>`,
    )
    .join("");

  const bodyRows = rows
    .map((row, idx) => {
      const notes = Array.isArray(row.raw?.discussionNotes)
        ? row.raw.discussionNotes.length
        : 0;
      const cells = [
        idx + 1,
        row.name || "—",
        row.patientId || "",
        row.enquiryId || "",
        row.phone && row.phone !== "—" ? row.phone : "",
        row.status || "—",
        row.stage || "—",
        row.waitingLabel || "—",
        row.priorityLabel || "—",
        notes,
      ]
        .map(
          (val) =>
            `<td style="padding:6px;border:1px solid #ddd;">${escapeHtmlCell(
              val,
            )}</td>`,
        )
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const excelHtml = `\uFEFF<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Aging Monitor</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<h3 style="color:#0ba6df;font-family:sans-serif;">Patient Aging Monitor</h3>
<table border="1" style="border-collapse:collapse;font-family:sans-serif;font-size:12px;">
<thead><tr>${headerRow}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body>
</html>`;

  const blob = new Blob([excelHtml], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.setAttribute("download", `patient-aging-monitor-${stamp}.xls`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadAgingCsv = (rows = []) => {
  const headers = [
    "Patient Name",
    "Patient ID",
    "Enquiry ID",
    "Phone Number",
    "Status",
    "Current Stage",
    "Waiting Time",
    "Priority",
    "Notes Count",
  ];
  const lines = [
    headers.join(","),
    ...rows.map((row) => {
      const notes = Array.isArray(row.raw?.discussionNotes)
        ? row.raw.discussionNotes.length
        : 0;
      return [
        escapeCsvCell(row.name),
        escapeCsvCell(row.patientId || ""),
        escapeCsvCell(row.enquiryId || ""),
        escapeCsvCell(row.phone && row.phone !== "—" ? row.phone : ""),
        escapeCsvCell(row.status),
        escapeCsvCell(row.stage),
        escapeCsvCell(row.waitingLabel),
        escapeCsvCell(row.priorityLabel),
        escapeCsvCell(notes),
      ].join(",");
    }),
  ];
  const blob = new Blob([`\uFEFF${lines.join("\n")}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.setAttribute("download", `patient-aging-monitor-${stamp}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Statuses excluded from Patient Aging Monitor */
const EXCLUDED_AGING_STATUSES = new Set([
  "local case",
  "passed away",
  "cancelled",
]);

const shouldShowInAgingMonitor = (row = {}) => {
  const status = String(row.p_status || row.status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  return !EXCLUDED_AGING_STATUSES.has(status);
};

const AGING_LEGEND_ORDER = ["normal", "warning", "high_priority", "critical"];
const AGING_PAGE_SIZE = 10;

const PRIORITY_STATIC_TREND = [2, 3, 2, 4, 3, 5, 4, 6, 5, 7, 8];

const PRIORITY_ITEMS = [
  {
    key: "newEnquiries",
    label: "New Enquiries",
    icon: "fa fa-user-plus",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "treatmentPlansPending",
    label: "Treatment Plans Pending",
    icon: "fa fa-file-text-o",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "followUpsDue",
    label: "Follow-ups Due",
    icon: "fa fa-refresh",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "doctorReviewsPending",
    label: "Doctor Reviews Pending",
    icon: "fa fa-stethoscope",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "patientsTravellingToday",
    label: "Patients Travelling Today",
    icon: "fa fa-plane",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "overdueCases",
    label: "Overdue Cases",
    icon: "fa fa-exclamation-triangle",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "arrivingToday",
    label: "Arriving Today",
    icon: "fas fa-plane-arrival",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  {
    key: "departingToday",
    label: "Departing Today",
    icon: "fas fa-plane-departure",
    staticTrend: PRIORITY_STATIC_TREND,
  },
  // {
  //   key: "preponedToday",
  //   label: "Preponed Today",
  //   icon: "fa fa-calendar-check-o",
  //   staticTrend: PRIORITY_STATIC_TREND,
  // },
  // {
  //   key: "postponedToday",
  //   label: "Postponed Today",
  //   icon: "fa fa-calendar-times-o",
  //   staticTrend: PRIORITY_STATIC_TREND,
  // },
];

const TODAYS_WORK_ITEMS = [
  {
    key: "newEnquiriesToday",
    label: "New enquiries today",
    icon: "fa fa-user-plus",
    color: "#0ba6df",
    fill: "#85B7EB",
  },
  {
    key: "patientsContactedToday",
    label: "Patients contacted today",
    icon: "fa fa-phone",
    color: "#0ba6df",
    fill: "#99F6E4",
  },
  {
    key: "treatmentPlansIssuedToday",
    label: "Treatment plans issued today",
    icon: "fa fa-file-text-o",
    color: "#534AB7",
    fill: "#AFA9EC",
  },
  {
    key: "appointmentsScheduledToday",
    label: "Appointments scheduled today",
    icon: "fa fa-calendar-check-o",
    color: "#854F0B",
    fill: "#FAC775",
  },
  {
    key: "patientsTravellingToday",
    label: "Patients travelling today",
    icon: "fa fa-plane",
    color: "#1D7A46",
    fill: "#A7F3D0",
  },
  {
    key: "followUpsDueToday",
    label: "Follow-ups due today",
    icon: "fa fa-refresh",
    color: "#A32D2D",
    fill: "#F7C1C1",
  },
];

const ATTENTION_ITEMS = [
  {
    key: "treatmentPlanOverdue",
    apiId: "treatment_plan_pending",
    label: "Waiting for treatment plan",
    icon: "fa fa-clock-o",
    color: "#0ba6df",
    fill: "#F7C1C1",
  },
  {
    key: "notContactedToday",
    apiId: "not_contacted_today",
    label: "Not contacted today",
    icon: "fa fa-phone-square",
    color: "#0ba6df",
    fill: "#FAC775",
  },
  {
    key: "overdueFollowUps",
    apiId: "overdue_followups",
    label: "Overdue follow-ups",
    icon: "fa fa-calendar-times-o",
    color: "#0ba6df",
    fill: "#F0997B",
  },
  {
    key: "doctorReviewPending",
    apiId: "doctor_review_pending",
    label: "Doctor review pending",
    icon: "fa fa-stethoscope",
    color: "#0ba6df",
    fill: "#85B7EB",
  },
  {
    key: "missingDocuments",
    apiId: "missing_documents",
    label: "Missing documents",
    icon: "fa fa-file-excel-o",
    color: "#0ba6df",
    fill: "#AFA9EC",
  },
  {
    key: "noStaffAssigned",
    apiId: "no_staff_assigned",
    label: "No staff assigned",
    icon: "fa fa-user-times",
    color: "#0ba6df",
    fill: "#B4B2A9",
  },
];

const IMMEDIATE_ATTENTION_META = {
  treatmentPlanOverdue: {
    urgency: "critical",
    urgencyLabel: "Critical",
    waiting: "26 – 48 hrs",
  },
  notContactedToday: {
    urgency: "high",
    urgencyLabel: "High Priority",
    waiting: "Since 9:00 AM",
  },
  overdueFollowUps: {
    urgency: "high",
    urgencyLabel: "High Priority",
    waiting: "1 – 5 days",
  },
  doctorReviewPending: {
    urgency: "warning",
    urgencyLabel: "Warning",
    waiting: "6 – 18 hrs",
  },
  missingDocuments: {
    urgency: "warning",
    urgencyLabel: "Warning",
    waiting: "12 – 40 hrs",
  },
  noStaffAssigned: {
    urgency: "normal",
    urgencyLabel: "Normal",
    waiting: "Since registration",
  },
};

const DEPARTMENT_CONFIG = [
  {
    key: "callCenter",
    apiId: "call_center",
    name: "Call Center",
    icon: "fa fa-phone",
    accent: "msd-dept-blue",
    defaultStats: [
      { label: "New Enquiries", key: "newEnquiries" },
      { label: "Contacted", key: "contacted" },
      { label: "Pending Contact", key: "pendingContact" },
    ],
  },
  {
    key: "medicalTeam",
    apiId: "medical_team",
    name: "Medical Team",
    icon: "fa fa-user-md",
    accent: "msd-dept-teal",
    defaultStats: [
      { label: "Waiting for Documents", key: "waitingForDocuments" },
      { label: "Cases Under Review", key: "casesUnderReview" },
      { label: "Treatment Plans Pending", key: "treatmentPlansPending" },
    ],
  },
  {
    key: "doctors",
    apiId: "doctors",
    name: "Doctors",
    icon: "fa fa-stethoscope",
    accent: "msd-dept-purple",
    defaultStats: [
      { label: "Cases Assigned", key: "casesAssigned" },
      { label: "Reviews Completed", key: "reviewsCompleted" },
      { label: "Reviews Pending", key: "reviewsPending" },
    ],
  },
  {
    key: "operations",
    apiId: "operations",
    name: "Operations",
    icon: "fa fa-plane",
    accent: "msd-dept-green",
    defaultStats: [
      { label: "Visa Pending", key: "visaPending" },
      { label: "Flights Pending", key: "flightsPending" },
      { label: "Travel This Week", key: "travelThisWeek" },
    ],
  },
];

const TODAYS_WORK_TARGETS = {
  newEnquiriesToday: 20,
  patientsContactedToday: 25,
  treatmentPlansIssuedToday: 10,
  appointmentsScheduledToday: 12,
  patientsTravellingToday: 6,
  followUpsDueToday: 15,
};

const mergeAttentionItems = (apiItems = []) => {
  const byId = Object.fromEntries((apiItems || []).map((item) => [item.id, item]));
  return ATTENTION_ITEMS.map((item) => {
    const api = byId[item.apiId];
    const meta = IMMEDIATE_ATTENTION_META[item.key] || {};
    return {
      ...item,
      count: api?.count ?? 0,
      timeRange: formatWaitingDuration(api?.timeRange || meta.waiting),
      urgency: meta.urgency,
      urgencyLabel: api?.priority || meta.urgencyLabel,
      viewCasesEndpoint: api?.viewCasesEndpoint,
    };
  });
};

const mergeDepartmentItems = (apiItems = []) => {
  const byId = Object.fromEntries((apiItems || []).map((item) => [item.id, item]));
  return DEPARTMENT_CONFIG.map((dept) => {
    const api = byId[dept.apiId];
    const metrics = api?.metrics || [];
    const stats = dept.defaultStats.map((stat) => {
      const metric = metrics.find((m) => m.key === stat.key);
      return {
        label: metric?.label || stat.label,
        value: metric?.value ?? 0,
      };
    });
    const total = stats.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
    const completed = stats[1]?.value ?? 0;
    return {
      ...dept,
      name: api?.title || dept.name,
      stats,
      completed: Number(completed) || 0,
      total: Number(total) || 1,
    };
  });
};

function buildSparklinePath(trend = [4, 6, 3, 7, 5, 8, 6]) {
  const width = 160;
  const height = 40;
  const points = trend.length > 1 ? trend : [4, 6, 3, 7, 5, 8, 6];
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((val, i) => {
    const x = i * step;
    const y = height - ((val - min) / range) * (height - 8) - 4;
    return [x, y];
  });
  let line = `M${coords[0][0]},${coords[0][1]}`;
  for (let i = 1; i < coords.length; i++) {
    line += ` L${coords[i][0]},${coords[i][1]}`;
  }
  return { line, area: `${line} L${width},${height} L0,${height} Z` };
}

function DashboardCards({
  items = [],
  counts = {},
  onCardClick = () => { },
  period = "Today",
}) {
  return (
    <div className="row gx-2 gy-2">
      {items.map((item) => {
        const { line, area } = buildSparklinePath(item.staticTrend);

        return (
          <div key={item.key} className="col-md-4">
            <div
              className="attention-card"
              onClick={() => onCardClick(item.key)}
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onCardClick(item.key);
              }}
            >
              <div className="attention-card-top">
                <div className="attention-icon">
                  <i className={item.icon}></i>
                </div>
                <div className="text-end attention-card-meta">
                  <span className="attention-label">{item.label}</span>
                  <h3 className="attention-count">
                    <CountUp
                      start={0}
                      end={Number(counts[item.key]) || 0}
                      duration={2}
                    />
                  </h3>
                </div>
              </div>
              <svg
                className="attention-sparkline"
                viewBox="0 0 160 40"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d={area} className="attention-sparkline-fill" />
                <path d={line} className="attention-sparkline-line" />
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const emptyDashboard = {
  dailySummary: {},
  todayPriorities: {},
  todayWork: {},
  immediateAttention: [],
  actionRequired: [],
  recentActivity: [],
  departmentPerformance: [],
  popupAlerts: [],
  patientAging: { total: 0, legend: {}, patients: [] },
  arrivingTodayCount: 0,
  departingTodayCount: 0,
  preponedTodayCount: 0,
  postponedTodayCount: 0,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const usrFount = localStorage.getItem("Role");
  const userRole = (localStorage.getItem("Role") || "").toUpperCase();
  const isReferralUser = userRole === "REFERRAL";
  const referralId = isReferralUser ? (localStorage.getItem("referral_id") || "") : "";

  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [caseModal, setCaseModal] = useState({ open: false, title: "", endpoint: "" });
  const [patientAgingData, setPatientAgingData] = useState([]);
  const [agingLoading, setAgingLoading] = useState(false);
  const [agingSearch, setAgingSearch] = useState("");
  const [agingStatusFilter, setAgingStatusFilter] = useState("");
  const [agingStageFilter, setAgingStageFilter] = useState("");
  const [agingPriorityFilter, setAgingPriorityFilter] = useState("");
  const [agingSortDesc, setAgingSortDesc] = useState(true);
  const [agingPage, setAgingPage] = useState(1);
  const [notesModal, setNotesModal] = useState({
    open: false,
    patientName: "",
    patientId: "",
    notes: [],
  });
  const [noteText, setNoteText] = useState("");
  const [noteDate, setNoteDate] = useState("");
  const [noteError, setNoteError] = useState({ note: false, date: false });
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [updatingStageId, setUpdatingStageId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [priorityData, setPriorityData] = useState({});
  const [prioritiesLoading, setPrioritiesLoading] = useState(true);
  const [crmPermissions, setCrmPermissions] = useState(null);

  const loadCrmPermissions = useCallback(async () => {
    try {
      const res = await fetchMyCrmDashboardPermissions();
      const list = extractPermissionsList(res);
      if (list) {
        setCrmPermissions(list);
      }
    } catch (err) {
      console.error("Failed to fetch my CRM dashboard permissions:", err);
    }
  }, []);

  useEffect(() => {
    if (!isReferralUser) {
      loadCrmPermissions();
    }
  }, [loadCrmPermissions, isReferralUser]);

  const loadPriorities = useCallback(async () => {
    setPrioritiesLoading(true);
    try {
      const [
        dailySummary,
        newEnqRes,
        treatmentPlansRes,
        followupsRes,
        doctorReviewsRes,
        travellingRes,
        overdueRes,
        arrivingRes,
        departingRes,
        preponedRes,
        postponedRes,
      ] = await Promise.all([
        fetchDailySummary().catch(() => ({})),
        fetchCaseList("dashboard/new-enquiries").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/treatment-plan-pending").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/followup-overdue").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/doctor-review-pending").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/patients-travelling-today").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/overdue-cases").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/patients-arriving-today").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/patients-departing-today").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/patients-preponed-today").catch(() => ({ count: 0 })),
        fetchCaseList("dashboard/patients-postponed-today").catch(() => ({ count: 0 })),
      ]);

      const counts = {
        newEnquiries:
          newEnqRes?.count ?? newEnqRes?.data?.length ?? dailySummary?.newEnquiries ?? 0,
        treatmentPlansPending:
          treatmentPlansRes?.count ?? treatmentPlansRes?.data?.length ?? dailySummary?.treatmentPlansPending ?? 0,
        followUpsDue:
          followupsRes?.count ?? followupsRes?.data?.length ?? dailySummary?.followupsDue ?? 0,
        doctorReviewsPending:
          doctorReviewsRes?.count ?? doctorReviewsRes?.data?.length ?? dailySummary?.doctorReviewsPending ?? 0,
        patientsTravellingToday:
          travellingRes?.count ?? travellingRes?.data?.length ?? dailySummary?.patientsTravellingToday ?? 0,
        overdueCases:
          overdueRes?.count ?? overdueRes?.data?.length ?? dailySummary?.overdueCases ?? 0,
        arrivingToday:
          arrivingRes?.count ?? arrivingRes?.data?.length ?? dailySummary?.arrivingToday ?? 0,
        departingToday:
          departingRes?.count ?? departingRes?.data?.length ?? dailySummary?.departingToday ?? 0,
        preponedToday:
          preponedRes?.count ?? preponedRes?.data?.length ?? dailySummary?.preponedToday ?? 0,
        postponedToday:
          postponedRes?.count ?? postponedRes?.data?.length ?? dailySummary?.postponedToday ?? 0,
      };

      setPriorityData(counts);
    } catch (err) {
      console.error("Priority data load failed:", err);
    } finally {
      setPrioritiesLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    // 1. Fetch Today's Priorities and CRM dashboard permissions immediately
    loadPriorities();
    loadCrmPermissions();

    // 2. Fetch full dashboard and aging monitor
    setLoading(true);
    setAgingLoading(true);
    try {
      const data = await fetchDashboard().catch(() => ({}));
      setDashboard((prev) => ({
        ...emptyDashboard,
        ...prev,
        ...data,
      }));

      const agingPatients = (data?.patientAging?.patients || []).filter(
        shouldShowInAgingMonitor,
      );
      setPatientAgingData(agingPatients.map(normalizeAgingRow));
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Dashboard load failed:", err);
    } finally {
      setLoading(false);
      setAgingLoading(false);
    }
  }, [loadPriorities, loadCrmPermissions]);

  useEffect(() => {
    if (!isReferralUser) {
      loadDashboard();
    }
  }, [loadDashboard, isReferralUser]);

  useEffect(() => {
    setAgingPage(1);
  }, [
    agingSearch,
    agingStatusFilter,
    agingStageFilter,
    agingPriorityFilter,
    agingSortDesc,
  ]);

  const openCaseList = (title, endpoint) => {
    if (!endpoint) return;
    setCaseModal({ open: true, title, endpoint });
  };

  const handlePriorityClick = (key) => {
    const endpointKey = key === "followUpsDue" ? "followupsDue" : key;
    openCaseList(
      PRIORITY_ITEMS.find((i) => i.key === key)?.label || "Cases",
      PRIORITY_ENDPOINTS[endpointKey] || PRIORITY_ENDPOINTS[key],
    );
  };

  const handleTodaysWorkClick = (key) => {
    openCaseList(
      TODAYS_WORK_ITEMS.find((i) => i.key === key)?.label || "Cases",
      TODAY_WORK_ENDPOINTS[key],
    );
  };

  const handleAttentionClick = (item) => {
    openCaseList(item.label, item.viewCasesEndpoint);
  };

  const openAgingNotes = (patient) => {
    const notes = Array.isArray(patient?.raw?.discussionNotes)
      ? patient.raw.discussionNotes
      : Array.isArray(patient?.discussionNotes)
        ? patient.discussionNotes
        : [];
    setNoteText("");
    setNoteDate(new Date().toISOString().slice(0, 10));
    setNoteError({ note: false, date: false });
    setNotesModal({
      open: true,
      patientName: patient?.name || "Patient",
      patientId: patient?.patientId || "",
      notes,
    });
  };

  const closeAgingNotes = () => {
    setNotesModal({
      open: false,
      patientName: "",
      patientId: "",
      notes: [],
    });
    setNoteText("");
    setNoteDate("");
    setNoteError({ note: false, date: false });
    setNoteSubmitting(false);
  };

  const handleSubmitAgingNote = async (e) => {
    e.preventDefault();
    const nextError = {
      note: !noteText.trim(),
      date: !noteDate,
    };
    setNoteError(nextError);
    if (nextError.note || nextError.date) return;

    const patientId = notesModal.patientId;
    if (!patientId) {
      toast.error("Patient ID missing — cannot add note");
      return;
    }

    setNoteSubmitting(true);
    try {
      await addPatientNotes(patientId, {
        note: noteText.trim(),
        date: noteDate,
      });
      const addedByName = localStorage.getItem("name") || "";
      const addedByRole = localStorage.getItem("Role") || "";
      const addedBy = addedByName && addedByRole ? `${addedByName} (${addedByRole})` : addedByName || addedByRole || "";
      const newNote = {
        note: noteText.trim(),
        date: noteDate,
        addedBy,
      };
      setNotesModal((prev) => ({
        ...prev,
        notes: [newNote, ...(Array.isArray(prev.notes) ? prev.notes : [])],
      }));
      setPatientAgingData((prev) =>
        prev.map((row) => {
          if (row.patientId !== patientId) return row;
          const existing = Array.isArray(row.raw?.discussionNotes)
            ? row.raw.discussionNotes
            : [];
          return {
            ...row,
            raw: {
              ...row.raw,
              discussionNotes: [newNote, ...existing],
            },
          };
        }),
      );
      setNoteText("");
      setNoteDate(new Date().toISOString().slice(0, 10));
      setNoteError({ note: false, date: false });
      toast.success("Note added successfully");
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to add note. Please try again!";
      toast.error(message);
    } finally {
      setNoteSubmitting(false);
    }
  };

  const [travelStatusModal, setTravelStatusModal] = useState({
    open: false,
    patientId: "",
    patientName: "",
    status: "",
  });
  const [travelStatusSubmitting, setTravelStatusSubmitting] = useState(false);

  const handleAgingStatusChange = async (patient, nextStatus) => {
    const statusValue = normalizePatientStatus(nextStatus);
    const current = normalizePatientStatus(patient?.status);
    if (!statusValue || statusValue === current) return;

    const patientId = patient?.patientId;
    if (!patientId) {
      toast.error("Patient ID missing — cannot update status");
      return;
    }

    if (isTravelStatus(statusValue)) {
      setTravelStatusModal({
        open: true,
        patientId,
        patientName: patient?.name || patient?.patient_name || "",
        status: statusValue,
      });
      return;
    }

    setUpdatingStatusId(patientId);
    try {
      const savedRaw = await changePatientStatus(patientId, statusValue);
      const savedStatus = normalizePatientStatus(savedRaw || statusValue);

      if (!shouldShowInAgingMonitor({ p_status: savedStatus })) {
        setPatientAgingData((prev) =>
          prev.filter((row) => row.patientId !== patientId),
        );
      } else {
        setPatientAgingData((prev) =>
          prev.map((row) => {
            if (row.patientId !== patientId) return row;
            return {
              ...row,
              status: savedStatus,
              raw: {
                ...row.raw,
                p_status: savedStatus,
              },
            };
          }),
        );
      }

      Swal.fire(
        "Success!",
        "Patient status updated successfully!",
        "success",
      );
    } catch (err) {
      console.error("Status update failed:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update status. Please try again!";
      toast.error(message);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleCloseTravelStatusModal = () => {
    if (travelStatusSubmitting) return;
    setTravelStatusModal({
      open: false,
      patientId: "",
      patientName: "",
      status: "",
    });
  };

  const handleSubmitTravelStatus = async (payload) => {
    const patientId = travelStatusModal.patientId;
    if (!patientId) return;
    setTravelStatusSubmitting(true);
    try {
      const savedRaw = await changePatientStatus(patientId, payload);
      const savedStatus = normalizePatientStatus(savedRaw || payload.p_status);

      if (!shouldShowInAgingMonitor({ p_status: savedStatus })) {
        setPatientAgingData((prev) =>
          prev.filter((row) => row.patientId !== patientId),
        );
      } else {
        setPatientAgingData((prev) =>
          prev.map((row) => {
            if (row.patientId !== patientId) return row;
            return {
              ...row,
              status: savedStatus,
              raw: {
                ...row.raw,
                p_status: savedStatus,
              },
            };
          }),
        );
      }

      setTravelStatusModal({
        open: false,
        patientId: "",
        patientName: "",
        status: "",
      });
      Swal.fire(
        "Success!",
        "Patient status updated successfully!",
        "success",
      );
    } catch (err) {
      console.error("Travel status update failed:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update travel status";
      toast.error(message);
    } finally {
      setTravelStatusSubmitting(false);
    }
  };

  const handleAgingStageChange = async (patient, nextStage) => {
    const stageLabel = normalizeAgingStage(nextStage);
    const current = normalizeAgingStage(patient?.stage);
    if (!stageLabel || stageLabel === current) return;

    const patientId = patient?.patientId;
    if (!patientId) {
      toast.error("Patient ID missing — cannot update stage");
      return;
    }

    const rowKey = patientId || patient?.id;
    setUpdatingStageId(rowKey);
    try {
      await updatePatientAgingStage(patientId, stageLabel);
      setPatientAgingData((prev) =>
        prev.map((row) => {
          if ((row.patientId || row.id) !== rowKey) return row;
          return {
            ...row,
            stage: stageLabel,
            raw: {
              ...row.raw,
              stageLabel,
            },
          };
        }),
      );
      Swal.fire("Success!", "Patient stage updated successfully!", "success");
      loadDashboard();
    } catch (err) {
      console.error("Stage update failed:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update stage. Please try again!";
      toast.error(message);
    } finally {
      setUpdatingStageId(null);
    }
  };

  const formatNoteDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDepartmentClick = (key) => {
    switch (key) {
      case "callCenter":
        navigate("/Admin/inquiry");
        break;
      case "medicalTeam":
      case "doctors":
      case "operations":
        navigate("/Admin/patients");
        break;
      default:
        break;
    }
  };

  const { dailySummary, todayPriorities, todayWork } = dashboard;

  const attentionItems = useMemo(
    () => mergeAttentionItems(dashboard.immediateAttention),
    [dashboard.immediateAttention],
  );
  const actionRequiredItems = useMemo(
    () => mergeActionRequiredItems(dashboard.actionRequired),
    [dashboard.actionRequired],
  );
  const departmentItems = useMemo(
    () => mergeDepartmentItems(dashboard.departmentPerformance),
    [dashboard.departmentPerformance],
  );

  const summaryCounts = {
    newEnquiries: dailySummary?.newEnquiries ?? 0,
    treatmentPlansPending: dailySummary?.treatmentPlansPending ?? 0,
    followupsDue: dailySummary?.followupsDue ?? 0,
    doctorReviewsPending: dailySummary?.doctorReviewsPending ?? 0,
    patientsTravellingToday: dailySummary?.patientsTravellingToday ?? 0,
    overdueCases: dailySummary?.overdueCases ?? 0,
  };

  const priorityCounts = {
    newEnquiries:
      priorityData.newEnquiries ?? todayPriorities?.newEnquiries ?? dailySummary?.newEnquiries ?? 0,
    treatmentPlansPending:
      priorityData.treatmentPlansPending ?? todayPriorities?.treatmentPlansPending ?? dailySummary?.treatmentPlansPending ?? 0,
    followUpsDue:
      priorityData.followUpsDue ?? todayPriorities?.followupsDue ?? dailySummary?.followupsDue ?? 0,
    doctorReviewsPending:
      priorityData.doctorReviewsPending ?? todayPriorities?.doctorReviewsPending ?? dailySummary?.doctorReviewsPending ?? 0,
    patientsTravellingToday:
      priorityData.patientsTravellingToday ?? todayPriorities?.patientsTravellingToday ?? dailySummary?.patientsTravellingToday ?? 0,
    overdueCases:
      priorityData.overdueCases ?? todayPriorities?.overdueCases ?? dailySummary?.overdueCases ?? 0,
    arrivingToday:
      priorityData.arrivingToday ?? dashboard.arrivingTodayCount ?? todayPriorities?.arrivingToday ?? dailySummary?.arrivingToday ?? 0,
    departingToday:
      priorityData.departingToday ?? dashboard.departingTodayCount ?? todayPriorities?.departingToday ?? dailySummary?.departingToday ?? 0,
    preponedToday:
      priorityData.preponedToday ?? dashboard.preponedTodayCount ?? todayPriorities?.preponedToday ?? dailySummary?.preponedToday ?? 0,
    postponedToday:
      priorityData.postponedToday ?? dashboard.postponedTodayCount ?? todayPriorities?.postponedToday ?? dailySummary?.postponedToday ?? 0,
  };

  // 1. Item-level filtering for Today's Priority
  const filteredPriorityItems = useMemo(() => {
    return PRIORITY_ITEMS.filter((item) =>
      isCrmDashboardItemAllowed(
        crmPermissions,
        null,
        "Today's Priority",
        item.label,
      ),
    );
  }, [crmPermissions]);

  const showTodaysPrioritySection = filteredPriorityItems.length > 0;

  // 2. Item-level filtering for Today's Work
  const filteredTodaysWorkItems = useMemo(() => {
    return TODAYS_WORK_ITEMS.filter((item) =>
      isCrmDashboardItemAllowed(
        crmPermissions,
        null,
        "Today's Work",
        item.label,
      ),
    );
  }, [crmPermissions]);

  const showTodaysWorkSection = filteredTodaysWorkItems.length > 0;

  // 3. Item-level filtering for Immediate Attention
  const filteredAttentionItems = useMemo(() => {
    return attentionItems.filter((item) =>
      isCrmDashboardItemAllowed(
        crmPermissions,
        null,
        "Immediate Attentions",
        item.label,
      ),
    );
  }, [attentionItems, crmPermissions]);

  const showImmediateAttentionSection = filteredAttentionItems.length > 0;

  // 4. Item-level filtering for Action Required
  const filteredActionRequiredItems = useMemo(() => {
    return actionRequiredItems.filter((item) =>
      isCrmDashboardItemAllowed(
        crmPermissions,
        null,
        "Action required",
        item.label,
      ),
    );
  }, [actionRequiredItems, crmPermissions]);

  const showActionRequiredSection = filteredActionRequiredItems.length > 0;

  // 5. Item-level filtering for Department Performance
  const filteredDepartmentItems = useMemo(() => {
    return departmentItems.filter((dept) =>
      isCrmDashboardItemAllowed(
        crmPermissions,
        null,
        "Department Performance",
        dept.name,
      ),
    );
  }, [departmentItems, crmPermissions]);

  const showDepartmentPerformanceSection = filteredDepartmentItems.length > 0;

  // 6. Section-level visibility for Patient Aging Monitor
  const showPatientAgingMonitor = useMemo(() => {
    return isCrmDashboardAnySectionAllowed(
      crmPermissions,
      "Patient Aging Monitor",
    );
  }, [crmPermissions]);

  const agingStatuses = useMemo(() => {
    const presentStatuses = patientAgingData
      .map((p) => p.status)
      .filter(Boolean);
    const optionsMap = new Map();
    PATIENT_STATUS_OPTIONS.forEach((opt) => {
      optionsMap.set(opt.value, opt.label);
    });
    presentStatuses.forEach((st) => {
      const norm = normalizePatientStatus(st) || st;
      if (norm && norm !== "—" && !optionsMap.has(norm)) {
        optionsMap.set(norm, getPatientStatusLabel(st) || st);
      }
    });
    return Array.from(optionsMap.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [patientAgingData]);

  const agingStages = useMemo(
    () => [
      ...new Set([
        ...PATIENT_AGING_STAGE_OPTIONS,
        ...patientAgingData.map((p) => p.stage).filter(Boolean),
      ]),
    ],
    [patientAgingData],
  );

  const agingLegendItems = useMemo(() => {
    const legend = dashboard.patientAging?.legend || {};
    return AGING_LEGEND_ORDER.filter((key) => legend[key]).map((key) => ({
      key,
      ...legend[key],
      dot: severityToDot("", legend[key].dotColor),
    }));
  }, [dashboard.patientAging?.legend]);

  const filteredAgingData = useMemo(() => {
    let rows = [...patientAgingData];
    if (agingSearch.trim()) {
      const q = agingSearch.trim().toLowerCase();
      rows = rows.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          String(p.patientId || "").toLowerCase().includes(q) ||
          String(p.enquiryId || "").toLowerCase().includes(q) ||
          String(p.phone || "").toLowerCase().includes(q),
      );
    }
    if (agingStatusFilter) {
      rows = rows.filter((p) => {
        const rowStatus = normalizePatientStatus(p.status) || p.status;
        const filterStatus =
          normalizePatientStatus(agingStatusFilter) || agingStatusFilter;
        return (
          String(rowStatus).trim().toLowerCase() ===
          String(filterStatus).trim().toLowerCase()
        );
      });
    }
    if (agingStageFilter) {
      rows = rows.filter((p) => p.stage === agingStageFilter);
    }
    if (agingPriorityFilter) {
      rows = rows.filter((p) => p.priority === agingPriorityFilter);
    }
    rows.sort((a, b) =>
      agingSortDesc
        ? b.waitingHours - a.waitingHours
        : a.waitingHours - b.waitingHours,
    );
    return rows;
  }, [
    patientAgingData,
    agingSearch,
    agingStatusFilter,
    agingStageFilter,
    agingPriorityFilter,
    agingSortDesc,
  ]);

  const handleExportAging = () => {
    if (!filteredAgingData.length) {
      toast.info("No patients to export");
      return;
    }
    downloadAgingExcel(filteredAgingData);
    toast.success("Patient Aging Monitor exported to Excel");
  };

  const agingTotalPages = Math.max(
    1,
    Math.ceil(filteredAgingData.length / AGING_PAGE_SIZE),
  );
  const safeAgingPage = Math.min(agingPage, agingTotalPages);

  const paginatedAgingData = useMemo(() => {
    const start = (safeAgingPage - 1) * AGING_PAGE_SIZE;
    return filteredAgingData.slice(start, start + AGING_PAGE_SIZE);
  }, [filteredAgingData, safeAgingPage]);

  const agingPageStart =
    filteredAgingData.length === 0
      ? 0
      : (safeAgingPage - 1) * AGING_PAGE_SIZE + 1;
  const agingPageEnd = Math.min(
    safeAgingPage * AGING_PAGE_SIZE,
    filteredAgingData.length,
  );

  if (isReferralUser) {
    return (
      <div className="main-wrapper">
        <Navbar onDashboardRefresh={() => {}} />
        <Sidebar />
        <div className="page-wrapper msd-scope">
          <div className="content">
            <div className="overview">
              <div className="row gx-0">
                <div className="col-md-12">
                  <div className="deash-heading">
                    <div>
                      <img src={dashboardImg} alt="" />
                    </div>
                    <div className="main-heading">
                      <h3>
                        Welcome {localStorage.getItem("name") || "Referral Partner"}
                      </h3>
                      <p className="mb-0">Have a nice day at work</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ReferralDashboard referralId={referralId} />
          </div>
        </div>
        <div
          className="sidebar-overlay"
          onClick={() => {
            const sidebar = document.getElementById("sidebar");
            const sidebarOverlay = document.querySelector(".sidebar-overlay");
            const mainWrapper = document.querySelector(".main-wrapper");
            if (sidebar) sidebar.classList.remove("opened");
            if (sidebarOverlay) sidebarOverlay.classList.remove("opened");
            if (mainWrapper) mainWrapper.classList.remove("slide-nav");
            document.documentElement.classList.remove("menu-opened");
          }}
          data-reff="#sidebar"
        />
      </div>
    );
  }

  return (
    <div className="main-wrapper">
      <Navbar onDashboardRefresh={loadDashboard} />
      <Sidebar />
      <div className="page-wrapper msd-scope">
        <div className="content">
          <div className="overview">
            <div className="row gx-0">
              <div className="col-md-12">
                <div className="deash-heading">
                  <div>
                    <img src={dashboardImg} alt="" />
                  </div>
                  <div className="main-heading">
                    <h3>Welcome {localStorage.getItem("name")}</h3>
                    <p className="mb-0">Have a nice day at work</p>
                  </div>
                </div>
              </div>


            </div>
          </div>

          {usrFount !== "Insurance Partner" && (
            <>

              <div className="row gx-2 gy-4">
                {showTodaysPrioritySection && filteredPriorityItems.length > 0 && (
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="treat-hd">
                          <h6>Today's Priorities</h6>
                          <span className="line"></span>
                        </div>
                        <DashboardCards
                          items={filteredPriorityItems}
                          counts={priorityCounts}
                          onCardClick={handlePriorityClick}
                          period="Today"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {showTodaysWorkSection && filteredTodaysWorkItems.length > 0 && (
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="treat-hd">
                          <h6>Today's Work</h6>
                          <span className="line"></span>
                        </div>
                      </div>
                    </div>
                    <div className="msd-work-grid">
                      {filteredTodaysWorkItems.map((item) => {
                        const metric = todayWork?.[item.key] || {};
                        const value = metric.count ?? 0;
                        const target =
                          metric.target ?? TODAYS_WORK_TARGETS[item.key] ?? 1;
                        return (
                          <div
                            key={item.key}
                            className="msd-work-card"
                            onClick={() => handleTodaysWorkClick(item.key)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleTodaysWorkClick(item.key);
                            }}
                          >
                            <div className="msd-work-card-top">
                              <div>
                                <p className="msd-work-label">{item.label}</p>
                                <h3 className="msd-work-count">
                                  <CountUp start={0} end={value} duration={2} />
                                </h3>
                              </div>
                              <div className="msd-work-icon">
                                <i className={item.icon}></i>
                              </div>
                            </div>
                            <div className="msd-work-footer">
                              {/* <span>
                              {value} of {target} target
                            </span> */}
                              <button
                                type="button"
                                className="msd-work-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTodaysWorkClick(item.key);
                                }}
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {showImmediateAttentionSection && filteredAttentionItems.length > 0 && (
                  <div className="col-md-12">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="treat-hd">
                          <h6>Immediate Attention</h6>
                          <span className="line"></span>
                        </div>
                      </div>
                    </div>
                    <div className="msd-attention-panel">
                      {filteredAttentionItems.map((item) => (
                        <div
                          key={item.key}
                          className={`msd-attention-row msd-urgency-${item.urgency || "normal"}`}
                          onClick={() => handleAttentionClick(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              handleAttentionClick(item);
                          }}
                        >
                          <div className="msd-attention-row-main">
                            <p className="msd-attention-row-title">{item.label}</p>
                            <span
                              className={`msd-badge msd-badge-${item.urgency || "normal"}`}
                            >
                              {item.urgencyLabel || "Normal"}
                            </span>
                          </div>
                          <div className="msd-attention-row-count">
                            <span className="msd-count-number">{item.count}</span>
                            <span className="msd-count-label">Patients</span>
                          </div>
                          <div className="msd-attention-row-waiting">
                            <i className="fa fa-hourglass-half"></i> {item.timeRange}
                          </div>
                          <button
                            type="button"
                            className="msd-view-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAttentionClick(item);
                            }}
                          >
                            View Cases <i className="fa fa-angle-right"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showActionRequiredSection && (
                  <div className="col-md-6">
                    <ActionRequiredSection items={filteredActionRequiredItems} />
                  </div>
                )}

                <div className={showActionRequiredSection ? "col-md-6" : "col-md-12"}>
                  <div className="treat-hd">
                    <h6>Recent Activity</h6>
                    <span className="line"></span>
                  </div>
                  <div className="msd-activity-list">
                    {(dashboard.recentActivity || []).length === 0 && (
                      <div className="msd-aging-empty p-3">No recent activity.</div>
                    )}
                    {(dashboard.recentActivity || []).map((a) => {
                      const patientLabel = (a.patientName || a.patient || "").trim();
                      const activityText = (a.activity || a.action || "").trim();
                      return (
                        <div className="msd-activity-item" key={a.id || a.performedAt}>
                          <div className="msd-activity-icon">
                            <i className={activityIcon(a.icon)}></i>
                          </div>
                          <div className="msd-activity-body">
                            <p className="msd-activity-text">
                              {patientLabel ? (
                                <>
                                  <strong>{patientLabel}</strong>
                                  {activityText ? ` — ${activityText}` : ""}
                                </>
                              ) : (
                                activityText || "—"
                              )}
                            </p>
                            <p className="msd-activity-meta">
                              {a.staffLabel || a.staffName || a.staff || "—"}
                            </p>
                          </div>
                          <div className="msd-activity-time">
                            {formatRecentActivityTime(a)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {showDepartmentPerformanceSection && (
                  <div className="col-md-12">
                    <div className="treat-hd">
                      <h6>Department Performance</h6>
                      <span className="line"></span>
                    </div>
                    <div className="msd-dept-grid">
                      {filteredDepartmentItems.map((dept) => (
                        <div
                          key={dept.key}
                          className={`msd-dept-card ${dept.accent}`}
                        >
                          <div className="msd-dept-head">
                            <div className="msd-dept-icon">
                              <i className={dept.icon}></i>
                            </div>
                            <h6 className="msd-dept-name">{dept.name}</h6>
                          </div>
                          <div className="msd-dept-stats">
                            {dept.stats.map((s) => (
                              <div className="msd-dept-stat-row" key={s.label}>
                                <span>{s.label}</span>
                                <span>{s.value}</span>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="msd-view-btn"
                            onClick={() => handleDepartmentClick(dept.key)}
                          >
                            View Department
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showPatientAgingMonitor && (
                  <div className="col-md-12">
                    <div className="treat-hd">
                      <h6>
                        Patient Aging Monitor
                        {patientAgingData.length
                          ? ` (${patientAgingData.length})`
                          : ""}
                      </h6>
                      <span className="line"></span>
                    </div>
                    <div className="main_content">
                      {agingLegendItems.length > 0 && (
                        <div className="msd-aging-legend">
                          {agingLegendItems.map((item) => (
                            <span key={item.key} className="msd-aging-legend-item">
                              <span
                                className={`msd-dot msd-dot-${item.dot}`}
                                aria-hidden="true"
                              />
                              {item.label}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="msd-aging-toolbar">
                        <input
                          type="text"
                          className="msd-aging-search"
                          placeholder="Search by name, ID, or phone..."
                          value={agingSearch}
                          onChange={(e) => setAgingSearch(e.target.value)}
                          aria-label="Search patients"
                        />
                        <select
                          className="msd-aging-select"
                          value={agingStatusFilter}
                          onChange={(e) => setAgingStatusFilter(e.target.value)}
                          aria-label="Filter by status"
                        >
                          <option value="">All Statuses</option>
                          {agingStatuses.map((st) => (
                            <option key={st.value} value={st.value}>
                              {st.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="msd-aging-select"
                          value={agingStageFilter}
                          onChange={(e) => setAgingStageFilter(e.target.value)}
                          aria-label="Filter by stage"
                        >
                          <option value="">All Stages</option>
                          {agingStages.map((stage) => (
                            <option key={stage} value={stage}>
                              {stage}
                            </option>
                          ))}
                        </select>
                        <select
                          className="msd-aging-select"
                          value={agingPriorityFilter}
                          onChange={(e) => setAgingPriorityFilter(e.target.value)}
                          aria-label="Filter by priority"
                        >
                          <option value="">All Priorities</option>
                          <option value="critical">Critical</option>
                          <option value="high">High Priority</option>
                          <option value="warning">Warning</option>
                          <option value="normal">Normal</option>
                        </select>
                        <button
                          type="button"
                          className="msd-aging-sort-btn"
                          onClick={() => setAgingSortDesc((prev) => !prev)}
                          title="Sort by waiting time"
                        >
                          <i
                            className={`fa fa-sort-amount-${agingSortDesc ? "desc" : "asc"}`}
                          ></i>{" "}
                          Longest Waiting
                        </button>
                        <button
                          type="button"
                          className="msd-aging-export-btn"
                          onClick={handleExportAging}
                          disabled={agingLoading || filteredAgingData.length === 0}
                          title="Export filtered patients to Excel"
                        >
                          <i className="fa fa-file-excel-o me-1" aria-hidden="true"></i>{" "}
                          Export Excel
                        </button>
                      </div>
                      <div className="table-responsive">
                        {agingLoading ? (
                          <div className="msd-aging-empty">Loading…</div>
                        ) : (
                          <table className="msd-aging-table">
                            <thead>
                              <tr>
                                <th>Patient Name</th>
                                <th>ID</th>
                                <th>Phone Number</th>
                                <th>Status</th>
                                <th>Current Stage</th>
                                <th>Waiting Time</th>
                                <th>Priority</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedAgingData.map((p, idx) => (
                                <tr
                                  key={`${p.patientId || p.enquiryId || p.id}-${idx}`}
                                  className={`msd-aging-row-${p.priority}`}
                                >
                                  <td>{p.name}</td>
                                  <td>{p.patientId || p.enquiryId || "—"}</td>
                                  <td title={p.phone}>{p.phone || "—"}</td>
                                  <td>
                                    {p.patientId ? (
                                      <select
                                        className={`msd-aging-status-select msd-status-${getPatientStatusBadgeKey(
                                          normalizePatientStatus(p.status) ||
                                          p.status,
                                        )}`}
                                        value={
                                          normalizePatientStatus(p.status) || ""
                                        }
                                        disabled={updatingStatusId === p.patientId}
                                        onChange={(e) =>
                                          handleAgingStatusChange(
                                            p,
                                            e.target.value,
                                          )
                                        }
                                        aria-label={`Change status for ${p.name}`}
                                      >
                                        <option value="" disabled>
                                          Select Status
                                        </option>
                                        {!PATIENT_STATUS_OPTIONS.some(
                                          (option) =>
                                            option.value ===
                                            normalizePatientStatus(p.status),
                                        ) &&
                                          p.status &&
                                          p.status !== "—" && (
                                            <option value={p.status}>
                                              {getPatientStatusLabel(p.status) ||
                                                p.status}
                                            </option>
                                          )}
                                        {PATIENT_STATUS_OPTIONS.map((option) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    ) : p.status && p.status !== "—" ? (
                                      <span
                                        className={`msd-status-badge msd-status-${getPatientStatusBadgeKey(p.status)}`}
                                      >
                                        {p.status}
                                      </span>
                                    ) : (
                                      "—"
                                    )}
                                  </td>
                                  <td>
                                    {p.patientId ? (
                                      <select
                                        className="msd-aging-stage-select"
                                        value={
                                          normalizeAgingStage(p.stage) || ""
                                        }
                                        disabled={
                                          updatingStageId ===
                                          (p.patientId || p.id)
                                        }
                                        onChange={(e) =>
                                          handleAgingStageChange(p, e.target.value)
                                        }
                                        aria-label={`Change current stage for ${p.name}`}
                                      >
                                        <option value="" disabled>
                                          Select stage
                                        </option>
                                        {!PATIENT_AGING_STAGE_OPTIONS.some(
                                          (option) =>
                                            option.toLowerCase() ===
                                            String(
                                              normalizeAgingStage(p.stage) ||
                                              p.stage ||
                                              "",
                                            ).toLowerCase(),
                                        ) &&
                                          p.stage &&
                                          p.stage !== "—" && (
                                            <option value={p.stage}>
                                              {p.stage}
                                            </option>
                                          )}
                                        {PATIENT_AGING_STAGE_OPTIONS.map(
                                          (stage) => (
                                            <option key={stage} value={stage}>
                                              {stage}
                                            </option>
                                          ),
                                        )}
                                      </select>
                                    ) : (
                                      p.stage
                                    )}
                                  </td>
                                  <td>{p.waitingLabel}</td>
                                  <td>
                                    <span
                                      className={`msd-badge msd-badge-${p.priority}`}
                                    >
                                      {p.priorityLabel}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="msd-aging-actions">
                                      <button
                                        type="button"
                                        className="msd-aging-view-btn msd-aging-notes-btn"
                                        title="View / Add Notes"
                                        onClick={() => openAgingNotes(p)}
                                        disabled={!p.patientId}
                                      >
                                        <i className="fa fa-sticky-note-o" aria-hidden="true"></i>
                                        Notes
                                        {Array.isArray(p.raw?.discussionNotes) &&
                                          p.raw.discussionNotes.length > 0
                                          ? ` (${p.raw.discussionNotes.length})`
                                          : ""}
                                      </button>
                                      <button
                                        type="button"
                                        className="msd-aging-view-btn"
                                        onClick={() => navigateToRecord(navigate, p.raw)}
                                      >
                                        {p.recordType === "enquiry"
                                          ? "View Enquiry"
                                          : "View Patient"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {!agingLoading && filteredAgingData.length === 0 && (
                          <div className="msd-aging-empty">
                            No patients match the selected filters.
                          </div>
                        )}
                      </div>
                      {!agingLoading && filteredAgingData.length > 0 && (
                        <div className="msd-aging-pagination">
                          <span className="msd-aging-page-info">
                            Showing {agingPageStart}–{agingPageEnd} of{" "}
                            {filteredAgingData.length}
                          </span>
                          <div className="msd-aging-page-controls">
                            <button
                              type="button"
                              className="msd-aging-page-btn"
                              disabled={safeAgingPage <= 1}
                              onClick={() =>
                                setAgingPage((page) => Math.max(1, page - 1))
                              }
                            >
                              Previous
                            </button>
                            <span className="msd-aging-page-num">
                              Page {safeAgingPage} of {agingTotalPages}
                            </span>
                            <button
                              type="button"
                              className="msd-aging-page-btn"
                              disabled={safeAgingPage >= agingTotalPages}
                              onClick={() =>
                                setAgingPage((page) =>
                                  Math.min(agingTotalPages, page + 1),
                                )
                              }
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* <DashboardAnalytics /> */}

            </>
          )}

        </div>
      </div>

      <CaseListModal
        isOpen={caseModal.open}
        title={caseModal.title}
        endpoint={caseModal.endpoint}
        onClose={() => setCaseModal({ open: false, title: "", endpoint: "" })}
        navigate={navigate}
      />

      {notesModal.open && (
        <div
          className="msd-modal-overlay"
          onClick={closeAgingNotes}
          role="presentation"
        >
          <div
            className="msd-modal-panel msd-notes-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="aging-notes-title"
          >
            <div className="msd-modal-head">
              <h5 id="aging-notes-title" className="msd-case-modal-title">
                Notes – {notesModal.patientName}
              </h5>
              {notesModal.patientId ? (
                <span className="msd-modal-count">{notesModal.patientId}</span>
              ) : null}
              <button
                type="button"
                className="msd-modal-close"
                onClick={closeAgingNotes}
                aria-label="Close"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="msd-modal-body">
              {notesModal.patientId ? (
                <form
                  className="msd-notes-form"
                  onSubmit={handleSubmitAgingNote}
                >
                  <div className="msd-notes-form-row">
                    <label htmlFor="aging-note-text">
                      Add Note<span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="aging-note-text"
                      className="msd-notes-textarea"
                      rows={3}
                      placeholder="Write patient note..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      disabled={noteSubmitting}
                    />
                    {noteError.note ? (
                      <span className="msd-notes-error">
                        Note cannot be empty
                      </span>
                    ) : null}
                  </div>
                  <div className="msd-notes-form-row">
                    <label htmlFor="aging-note-date">
                      Date<span className="text-danger">*</span>
                    </label>
                    <input
                      id="aging-note-date"
                      type="date"
                      className="msd-notes-date"
                      value={noteDate}
                      onChange={(e) => setNoteDate(e.target.value)}
                      disabled={noteSubmitting}
                    />
                    {noteError.date ? (
                      <span className="msd-notes-error">Please select a date</span>
                    ) : null}
                  </div>
                  <div className="msd-notes-form-actions">
                    <button
                      type="submit"
                      className="msd-aging-view-btn"
                      disabled={noteSubmitting}
                    >
                      {noteSubmitting ? "Saving..." : "Add Note"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="msd-aging-empty">
                  Patient ID missing — notes cannot be added.
                </div>
              )}

              <div className="msd-notes-divider">Existing Notes</div>

              {notesModal.notes.length === 0 ? (
                <div className="msd-aging-empty">No notes for this patient.</div>
              ) : (
                <div className="msd-notes-list">
                  {notesModal.notes.map((item, index) => (
                    <div
                      key={item.id || `${item.date}-${index}`}
                      className="msd-notes-item"
                    >
                      <div className="msd-notes-item-head">
                        <strong>Note-{index + 1}</strong>
                        <span>
                          {formatNoteDate(item.date)}
                          {item.addedBy && ` | ${item.addedBy}`}
                        </span>
                      </div>
                      <p className="msd-notes-item-text">{item.note || "—"}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <TravelStatusModal
        open={travelStatusModal.open}
        status={travelStatusModal.status}
        patientId={travelStatusModal.patientId}
        patientName={travelStatusModal.patientName}
        onClose={handleCloseTravelStatusModal}
        onSubmit={handleSubmitTravelStatus}
        submitting={travelStatusSubmitting}
      />
      <div
        className="sidebar-overlay"
        onClick={() => {
          const sidebar = document.getElementById('sidebar');
          const sidebarOverlay = document.querySelector('.sidebar-overlay');
          const mainWrapper = document.querySelector('.main-wrapper');
          if (sidebar) sidebar.classList.remove('opened');
          if (sidebarOverlay) sidebarOverlay.classList.remove('opened');
          if (mainWrapper) mainWrapper.classList.remove('slide-nav');
          document.documentElement.classList.remove('menu-opened');
        }}
        data-reff="#sidebar"
      />
    </div>
  );
}
